import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import { SelectionBaseT } from 'business/definitions';
import {
    getExpandedRowIds,
    getNodeIdsFromSelection,
    getSelectionTree,
    selectTreeNode,
} from 'helpers/treeHelpers';
import SelectableTree from 'ui/Tree/SelectableTree';

import { getTreeWithIsDisabledField } from '../../helpers/permission';

const StyDialog = styled(Dialog, {
    name: 'StyDialog',
})({
    '.MuiPaper-root': {
        height: '100%',
    },
});

type Props = {
    onClose: () => void;
    title: string;
    tree: TreeT;
    initialSelectedValues: SelectionBaseT[];
    submit: (nodeIds: SelectionBaseT[]) => void;
    show: boolean;
};

function PermissionValuesModal({
    tree,
    initialSelectedValues,
    title,
    show,
    onClose,
    submit: submitProps,
}: Props) {
    const { t } = useTranslation();
    const dialogTitle = `${t('treeTableModal.filterOn')} ${title}`;

    const initialSelectedNodeIds: number[] = useMemo(
        () => getNodeIdsFromSelection(tree, initialSelectedValues),
        [tree, initialSelectedValues],
    );

    const [selectedNodeIds, setSelectedNodeIds] = useState<number[]>(
        initialSelectedNodeIds,
    );

    const cancel = useCallback(() => {
        onClose();
        setSelectedNodeIds(initialSelectedNodeIds);
    }, [initialSelectedNodeIds, onClose]);

    const handleSubmit = useCallback(() => {
        const selectionTree = getSelectionTree(tree, selectedNodeIds);

        submitProps(selectionTree);
    }, [selectedNodeIds, submitProps, tree]);

    const defaultExpandedIds = useMemo(
        () => getExpandedRowIds(tree, initialSelectedValues),
        [initialSelectedValues, tree],
    );

    const handleNodeSelection = (node: TreeT) => {
        selectTreeNode(tree, node, setSelectedNodeIds);
    };

    const treeWithIsDisableField = useMemo(
        () => getTreeWithIsDisabledField(tree, selectedNodeIds),
        [tree, selectedNodeIds],
    );

    return (
        <StyDialog
            open={show}
            maxWidth="md"
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            }}
            fullWidth
            disableEscapeKeyDown
        >
            <DialogTitle variant="h5">{dialogTitle}</DialogTitle>

            <DialogContent>
                <SelectableTree
                    tree={treeWithIsDisableField}
                    withSearch
                    withSelectAll
                    selectedNodeIds={selectedNodeIds}
                    setSelectedNodeIds={setSelectedNodeIds}
                    defaultExpandedIds={defaultExpandedIds}
                    handleNodeSelect={handleNodeSelection}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel}>{t('common.cancel')}</Button>
                <Button
                    data-testid="e2e-submit-tree-table"
                    onClick={handleSubmit}
                >
                    {t('common.confirm')}
                </Button>
            </DialogActions>
        </StyDialog>
    );
}

export default PermissionValuesModal;
